// src/theme.ts
import { extendTheme } from "@chakra-ui/react";
import { palettes } from "@cofit/palettes";
import { deepmerge } from "deepmerge-ts";
var breakpoints = {
  sm: "320px",
  md: "768px",
  lg: "1024px",
  xl: "1441px",
  "2xl": "1920px"
};

var TabGhostBaseStyle = function TabGhostBaseStyle(_ref) {
  var c = _ref.colorScheme;
  return {
    color: "gray.700",
    py: 1,
    borderBottom: "2px",
    borderColor: "gray.100",
    _selected: {
      fontWeight: "semibold",
      color: "".concat(c, ".500"),
      bg: "transparent",
      borderColor: "currentColor",
      _hover: {
        fontWeight: "semibold",
        color: "".concat(c, ".500"),
        bg: "transparent",
        borderColor: "currentColor"
      }
    },
    _disabled: {
      color: "gray.400",
      bg: "transparent",
      pointerEvents: "none"
    },
    _hover: {
      color: "".concat(c, ".300"),
      bg: "transparent",
      borderColor: "currentColor"
    },
    _active: {
      color: "".concat(c, ".500"),
      fontWeight: "normal",
      bg: "transparent",
      borderColor: "currentColor"
    }
  };
};

var TabRoundedStyle = function TabRoundedStyle(_ref2) {
  var c = _ref2.colorScheme;
  return deepmerge({}, TabGhostBaseStyle({
    colorScheme: c
  }), {
    color: "gray.700",
    rounded: "md",
    borderBottom: "transparent",
    px: 6,
    py: 1,
    _hover: {
      color: "gray.700",
      bg: "".concat(c, ".50")
    },
    _active: {
      color: "gray.700",
      bg: "".concat(c, ".100"),
      _hover: {
        color: "gray.700",
        bg: "".concat(c, ".100")
      }
    },
    _selected: {
      color: "white",
      bg: "".concat(c, ".500"),
      _hover: {
        color: "white",
        bg: "".concat(c, ".500")
      }
    },
    _disabled: {
      color: "gray.500",
      bg: "gray.100"
    }
  });
};

var TabTagStyle = function TabTagStyle(_ref3) {
  var c = _ref3.colorScheme;
  return deepmerge({}, TabRoundedStyle({
    colorScheme: c
  }), {
    borderRadius: "3xl",
    px: 3.5
  });
};

var theme = extendTheme({
  fonts: {
    heading: "'Noto Sans TC', 'Noto Sans', Arial, Helvetica, sans-serif",
    body: "'Noto Sans TC', 'Noto Sans', Arial, Helvetica, sans-serif;",
    calendarHeading: "'Montserrat', Arial, Helvetica, sans-serif"
  },
  layerStyles: {
    base: {
      bg: "white",
      borderRadius: "6px",
      shadow: "0px 4px 24px rgba(0, 0, 0, 0.06);"
    },
    selected: {
      bg: "teal.500",
      color: "teal.700",
      borderColor: "orange.500"
    }
  },
  sizes: {
    10.5: "2.625rem",
    11: "2.75rem"
  },
  colors: palettes,
  components: {
    Button: {
      baseStyle: function baseStyle(_ref4) {
        var c = _ref4.colorScheme;
        return {
          borderRadius: "md",
          fontWeight: "normal",
          fontSize: "md",
          bg: "transparent",
          color: "".concat(c, ".brand")
        };
      },
      variants: {
        solid: function solid(_ref5) {
          var c = _ref5.colorScheme;
          return {
            bg: "".concat(c, ".brand"),
            color: "white",
            _hover: {
              bg: "".concat(c, ".500")
            },
            _active: {
              bg: "".concat(c, ".700")
            },
            _selected: {
              bg: "".concat(c, ".700")
            },
            _disabled: {
              bg: "gray.100",
              color: "gray.500",
              pointerEvents: "none"
            }
          };
        },
        outline: function outline(_ref6) {
          var c = _ref6.colorScheme;
          return {
            color: "".concat(c, ".brand"),
            bg: "transparent",
            border: "1px",
            borderColor: "".concat(c, ".brand"),
            _hover: {
              bg: "".concat(c, ".50")
            },
            _active: {
              bg: "".concat(c, ".100")
            },
            _selected: {
              bg: "".concat(c, ".100")
            },
            _disabled: {
              bg: "gray.100",
              color: "gray.500",
              border: "none",
              pointerEvents: "none"
            }
          };
        },
        ghost: function ghost(_ref7) {
          var c = _ref7.colorScheme;
          return {
            color: "".concat(c, ".brand"),
            bg: "transparent",
            _hover: {
              bg: "".concat(c, ".50")
            },
            _active: {
              bg: "".concat(c, ".100")
            },
            _selected: {
              bg: "".concat(c, ".100")
            },
            _disabled: {
              color: "gray.500",
              pointerEvents: "none"
            }
          };
        },
        link: function link(_ref8) {
          var c = _ref8.colorScheme;
          return {
            color: "".concat(c, ".brand"),
            _hover: {
              color: "".concat(c, ".500")
            },
            _active: {
              color: "".concat(c, ".700")
            },
            _selected: {
              color: "".concat(c, ".700")
            },
            _disabled: {
              color: "gray.500",
              pointerEvents: "none"
            }
          };
        }
      },
      sizes: {
        lg: {
          px: "6",
          h: "10.5"
        },
        md: {
          px: "6",
          h: "8"
        },
        sm: {
          px: "2.5",
          h: "8"
        }
      },
      defaultProps: {
        colorScheme: "orange",
        size: "md"
      }
    },
    Tabs: {
      variants: {
        ghost: function ghost(_ref9) {
          var colorScheme = _ref9.colorScheme;
          return {
            tab: TabGhostBaseStyle({
              colorScheme: colorScheme
            })
          };
        },
        rounded: function rounded(_ref10) {
          var colorScheme = _ref10.colorScheme;
          return {
            tab: TabRoundedStyle({
              colorScheme: colorScheme
            })
          };
        },
        tag: function tag(_ref11) {
          var colorScheme = _ref11.colorScheme;
          return {
            tab: TabTagStyle({
              colorScheme: colorScheme
            })
          };
        }
      }
    },
    Tooltip: {
      baseStyle: {
        borderRadius: "md",
        fontWeight: "normal"
      }
    }
  },
  breakpoints: breakpoints
});
export { theme };